import { CommonModule, CurrencyPipe } from "@angular/common";
import { Component, EventEmitter, inject, Input, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { EventComboTicket } from "@models/combo-ticket";
import { SnackBarService } from "@services/snack-bar.service";
import { finalize, take } from "rxjs";
import { ButtonComponent } from "../../../../../../shared/components/button/button.component";
import { EventTicketBatch } from "../../../../../../shared/components/modal-tickets/types/ticket-batch.type";
import {
  ComboEditDialogData,
  ComboEditModalComponent,
} from "./combo-edit-modal/combo-edit-modal.component";

@Component({
  selector: "app-event-combo-step",
  imports: [CommonModule, CurrencyPipe, ButtonComponent],
  templateUrl: "./event-combo-step.component.html",
  styleUrl: "./event-combo-step.component.scss",
})
export class EventComboStepComponent {
  private readonly dialog = inject(MatDialog);
  private readonly snackbarService = inject(SnackBarService);
  private dialogOpen = false;

  @Input({ required: true }) combos: EventComboTicket[] = [];
  @Input() ticketBatches: EventTicketBatch[] = [];
  @Output() combosChange = new EventEmitter<EventComboTicket[]>();

  get hasEnoughIndividualTickets(): boolean {
    return this.ticketBatches.flatMap((batch) => batch.tickets ?? []).length >= 1;
  }

  openComboModal(combo?: EventComboTicket): void {
    if (this.dialogOpen) return;
    this.dialogOpen = true;
    this.dialog
      .open(ComboEditModalComponent, {
        width: "700px",
        maxWidth: "95vw",
        maxHeight: "90vh",
        autoFocus: "dialog",
        restoreFocus: true,
        disableClose: true,
        panelClass: "combo-edit-dialog",
        data: { combo, ticketBatches: this.ticketBatches } as ComboEditDialogData,
      })
      .afterClosed()
      .pipe(
        take(1),
        finalize(() => (this.dialogOpen = false)),
      )
      .subscribe((result: EventComboTicket | undefined) => {
        if (!result) return;
        if (combo) {
          this.combosChange.emit(
            this.combos.map((item) => (item === combo ? result : item)),
          );
        } else {
          this.combosChange.emit([...this.combos, result]);
        }
      });
  }

  removeCombo(combo: EventComboTicket): void {
    if (combo.total_distributed && combo.total_distributed > 0) {
      this.snackbarService.openSnackBar(
        "Não é possível remover um ingresso combo que já possui vendas.",
        "Fechar",
        "error",
      );
      return;
    }
    this.combosChange.emit(this.combos.filter((item) => item !== combo));
  }

  comboLoteLabel(combo: EventComboTicket): string {
    if (combo.source_batch_number) return `Lote ${combo.source_batch_number}`;
    const index = this.ticketBatches.findIndex(
      (batch) => Number(batch.id) === Number(combo.source_batch_id),
    );
    return index >= 0 ? `Lote ${index + 1}` : "";
  }

  componentNames(combo: EventComboTicket): string {
    return (combo.component_tickets ?? [])
      .map((ticket) => {
        const quantity = Math.max(1, Number(ticket.quantity ?? 1));
        return quantity > 1 ? `${quantity}× ${ticket.title}` : ticket.title;
      })
      .join(" + ");
  }
}
